import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "../../../../utils/api";
import { useNavigate } from "react-router-dom";

const REASONS = [
  "Advisor Side Edit(s) Required",
  "Partial Acceptance from Client",
  "Client Not Interested/Unresponsive",
  "Declined by Client",
];

const WHY_OPTIONS = [
  "Taxation Concerns",
  "Not Happy with Fund Recommendation(s)",
  "Not Happy with Target Asset Allocation",
  "Does not view it as relevant as goals are long-term",
];

export const DiscardReasonModal = ({
  advisorId,
  setSelectedTab,
  setHistoryActiveTab,
  show,
  onHide,
  isPortfolioData,
  onSuccess,
  fetchPortfolioData,
  setActiveTab
}) => {
  const [discardReason, setDiscardReason] = useState("");
  const [discartwhy, setDiscartwhy] = useState(""); // NEW
  const [remark, setRemark] = useState("");
  const [errors, setErrors] = useState({}); // { discardReason?, discartwhy?, remark? }
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search);
  const roomId = queryParams.get("clientId");

  // Defensive IDs from parent data
  const clientId = isPortfolioData?.prinfo?.client_id;
  const prId = isPortfolioData?.prinfo?.portfolio_review_id;

  // Reset everything when modal closes
  useEffect(() => {
    if (!show) {
      setDiscardReason("");
      setDiscartwhy("");
      setRemark("");
      setErrors({});
      setSubmitting(false);
    }
  }, [show]);

  // Local validation (conditional for discartwhy)
  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!discardReason) newErrors.discardReason = "Please select a reason.";
    if (discardReason === "Declined by Client" && !discartwhy) {
      newErrors.discartwhy = "Please select why the client declined.";
    }
    if (!remark) newErrors.remark = "Please provide a brief remark.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [discardReason, discartwhy, remark]);

  const handleDiscardReasonChange = (e) => {
    const value = e.target.value;
    setDiscardReason(value);
    setErrors((prev) => ({ ...prev, discardReason: "" }));
    // If user changes away from "Declined by Client", clear discartwhy
    if (value !== "Declined by Client") {
      setDiscartwhy("");
      setErrors((prev) => ({ ...prev, discartwhy: "" }));
    }
  };

  const handleWhyChange = (e) => {
    setDiscartwhy(e.target.value);
    if (e.target.value) setErrors((prev) => ({ ...prev, discartwhy: "" }));
  };

  const handleRemarkChange = (e) => {
    setRemark(e.target.value);
    if (e.target.value) setErrors((prev) => ({ ...prev, remark: "" }));
  };

  const canSubmit = useMemo(() => {
    const base = !!discardReason && !!remark && !submitting;
    if (discardReason === "Declined by Client") {
      return base && !!discartwhy;
    }
    return base;
  }, [discardReason, discartwhy, remark, submitting]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (!clientId || !prId) {
      Swal.fire({
        text: "Missing client or review info.",
        confirmButtonText: "OK",
        customClass: { confirmButton: "swal-confirm-btn" },
      });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        clientid: clientId,
        advisorId,
        pr_id: prId,
        discardReason,
        ...(discardReason === "Declined by Client" && { discartwhy }), // conditional field
        actionfrom: "submit",
        discardRemark: remark,
        discardremark: remark, // if backend expects both
      };

      const { data } = await api.post("advisor/draftportfolio", payload);
      const { status, errors: serverErrors } = data || {};

      if (!status) {
        Swal.fire({
          icon: "error",
          text: serverErrors?.common || "Discard failed. Please try again.",
          confirmButtonText: "OK",
          customClass: { confirmButton: "swal-confirm-btn" },
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Discarded",
        text: "Portfolio review discarded successfully.",
        confirmButtonText: "OK",
        customClass: { confirmButton: "swal-confirm-btn" },
      }).then(() => {
        onHide?.();
        onSuccess?.(data);
        fetchPortfolioData();

        if (payload.discardReason !== "Advisor Side Edit(s) Required") {
          setHistoryActiveTab("Portfolio");
          setActiveTab("Event-tab");
        } else {
          setSelectedTab(1);
        }
      });

    } catch (err) {
      console.error("Submit failed:", err);
      Swal.fire({
        icon: "error",
        text: "Network error while submitting.",
        confirmButtonText: "OK",
        customClass: { confirmButton: "swal-confirm-btn" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => onHide?.();

  return (
    <Modal show={show} onHide={handleClose} className="custom-sm-modal" centered>
      <Modal.Header closeButton style={{ backgroundColor: "#fff", border: "none" }}>
        <Modal.Title>Discard Reason?</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Discard Reason */}
          <Form.Group controlId="discardReason_div">
            <Form.Label>Select Reason</Form.Label>
            <Form.Select
              value={discardReason}
              onChange={handleDiscardReasonChange}
              isInvalid={!!errors.discardReason}
              disabled={submitting}
            >
              <option value="">Select Reason</option>
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.discardReason}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Why (conditional) */}
          {discardReason === "Declined by Client" && (
            <Form.Group controlId="discartwhy_div" className="mt-3 s">
              <Form.Label>Select Why</Form.Label>
              <Form.Select
                name="discartwhy"
                value={discartwhy}
                onChange={handleWhyChange}
                isInvalid={!!errors.discartwhy}
                disabled={submitting}
                className="highlightelement"
              >
                <option value="">Select Why</option>
                {WHY_OPTIONS.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.discartwhy}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          {/* Remark */}
          <Form.Group controlId="remarkdiscart_div" className="mt-3">
            <Form.Label>Brief Remark</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Enter remark"
              value={remark}
              onChange={handleRemarkChange}
              isInvalid={!!errors.remark}
              disabled={submitting}
              rows={3}
            />
            <Form.Control.Feedback type="invalid">
              {errors.remark}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <button className="lrgbutton text-white" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" /> Submitting...
            </>
          ) : (
            "Submit"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
